// Post-success snackbar label derivation extracted from the callPostEntityData
// method of components/defaultLayout/EntityDialog&Forms/EntityDialog.vue. Pulled
// into a pure, tested module for the Nuxt 2 -> Nuxt 3 / Vuex -> Pinia migration;
// the component now calls this to build the snackbar fragment.

import { isEntityDataItem } from '~/types/models'
import { AfterRequestCallbackError } from '~/cynqueModules/saveEntityData'
import {
  ValidationError,
  PayloadTooLargeError,
  NetworkError,
  DeleteConflictError,
} from '~/services/errors'
import { formatBytes } from '~/cynqueModules/formatBytes'
import {
  DELETE_CONFLICT_MESSAGE,
  VALIDATION_REJECTED_MESSAGE,
  toDisplayMessage,
} from '~/cynqueModules/handleAsyncDataErrors'

// A parenthetical-free display fragment naming the just-saved instance, e.g.
// ` 'Acme'`, used inside "New Company 'Acme' created successfully." Prefers the
// entity's `name`, falls back to `descr`, and is '' when neither is present or
// the payload is not an entity data item.
//
// NOTE (documented kludge, preserved verbatim): the descr fallback exists
// because `company` has no `descr`; see the original comment in EntityDialog.vue.
export function deriveEntityInstanceLabel(entityData: any): string {
  if (isEntityDataItem(entityData)) {
    return entityData.name
      ? ` '${entityData.name}'`
      : entityData.descr
      ? ` '${entityData.descr}'`
      : ''
  }
  return ''
}

/** The reasons a send was refused, from a 400's body, or []. */
export function sendProblemsFrom(body: unknown): string[] {
  const problems = (body as { problems?: unknown } | null | undefined)?.problems
  return Array.isArray(problems)
    ? problems.filter(
        (problem): problem is string =>
          typeof problem === 'string' && !!problem.trim()
      )
    : []
}

export interface ISaveErrorNotification {
  type: string
  message: string
  statusCode?: number
}

/**
 * The user-facing notification for a failed save.
 *
 * Extracted from EntityDialog.vue's `notificationForSaveError` so the branching
 * is pure and testable. The cases are deliberately worded differently:
 *
 * 1. `AfterRequestCallbackError` — the write SUCCEEDED and the refresh failed.
 *    Wording unchanged; telling the user it failed sends them off to re-enter
 *    work that is already on the server.
 * 2. `ValidationError` — the API rejected the payload. Says so plainly, and
 *    passes `statusCode` through: `addNotification` has always accepted one and
 *    NotificationBar already renders it, but this builder never returned one.
 *    The API's own `detail` and `extra[].key` are deliberately NOT shown —
 *    `detail` names the endpoint and the keys are pydantic identifiers like
 *    `split_fraction`. Those go to the console and Honeybadger. The exception
 *    is an email's refused SEND, whose `problems` are written for the user.
 * 3. `NetworkError` — the connection dropped, so the outcome is UNKNOWN. The
 *    request may have reached the server and committed before the response was
 *    lost (production, 2026-10-26: a product that "couldn't be created" existed).
 *    httpPost already looks a lost create up by its id and returns it as a
 *    success when it finds it; reaching here means that lookup could not
 *    confirm it, which is still not proof of failure. "Couldn't create" sent the
 *    user off to enter it again — a duplicate — so say what is actually known.
 * 4. `DeleteConflictError` — the API refused the delete because other records
 *    still refer to the row (a 409). Says so in `DELETE_CONFLICT_MESSAGE`'s
 *    words; the API's own `detail` is a database hint — production, 2026-10-27
 *    (Honeybadger 135244466), a user read "Opportunity team members with
 *    has_owner = false exist for this opportunity (409 on DELETE /api/...)".
 * 5. Anything else — `error.message` rather than `${e}`, which drops the
 *    "Error: " prefix template interpolation used to add, with non-Errors routed
 *    through `toDisplayMessage`. That is what `toDisplayMessage` was written for
 *    and could never achieve at the display layer while the http verbs were
 *    handing it an already-coerced '[object Object]'.
 *
 * ⚠ `entityLabel` must be the USER-FACING label, never `entityName`. The dialog
 * rendered the raw identifier, so a user literally read
 * "Couldn't create opportunityMSTeamMember". cynqueModules/userFacingTerminology.ts
 * cannot catch that leak — it scans literal `message:` strings, and this one is
 * interpolated at runtime.
 */
export function saveErrorNotification({
  error,
  verb,
  entityLabel,
}: {
  error: unknown
  verb: 'create' | 'update' | 'delete'
  entityLabel: string
}): ISaveErrorNotification {
  if (error instanceof AfterRequestCallbackError) {
    const pastTense = verb === 'delete' ? 'deleted' : `${verb}d`
    return {
      type: 'warning',
      message:
        `${entityLabel} was ${pastTense} successfully, but this screen` +
        " couldn't be refreshed. Reload the page to see the change.",
    }
  }

  if (error instanceof PayloadTooLargeError) {
    // Unlike the ValidationError case above, the API's own wording is useless
    // here and ours is actionable, so this DOES name specifics -- but it takes
    // them from the error's own fields, not from `.message`, which is kept
    // file-agnostic so Honeybadger groups these as one fault.
    const size =
      error.fileSize === undefined ? '' : ` is ${formatBytes(error.fileSize)}`
    const subject = error.fileName ?? 'That file'
    return {
      type: 'warning',
      message:
        `Couldn't ${verb} ${entityLabel} — ${subject}${size}.` +
        ` The maximum upload size is ${formatBytes(error.maxBytes)}.`,
      statusCode: error.statusCode ?? undefined,
    }
  }

  if (error instanceof ValidationError) {
    // The one 400 whose wording IS for the user: an email's send refused, with
    // every reason at once (cynque-api-server #1770, `composition_problems`,
    // which never names an address). Litestar merges a ProblemDetails `extra`
    // mapping into the top level of the body, so `problems` arrives there.
    const problems = sendProblemsFrom(error.body)
    if (problems.length) {
      return {
        type: 'warning',
        message: `Couldn't send the ${entityLabel} — ${problems.join(' ')}`,
        statusCode: error.statusCode ?? undefined,
      }
    }
    return {
      type: 'warning',
      message: `Couldn't ${verb} ${entityLabel} — ${VALIDATION_REJECTED_MESSAGE}`,
      statusCode: error.statusCode ?? undefined,
    }
  }

  if (error instanceof DeleteConflictError) {
    return {
      type: 'warning',
      message: `Couldn't ${verb} ${entityLabel} — ${DELETE_CONFLICT_MESSAGE}`,
      statusCode: error.statusCode ?? undefined,
    }
  }

  if (error instanceof NetworkError) {
    const pastTense = verb === 'delete' ? 'deleted' : `${verb}d`
    const check =
      verb === 'create'
        ? 'Check before entering it again.'
        : 'Reload the page to check.'
    return {
      type: 'warning',
      message:
        `The connection to the server was lost while saving, so the ${entityLabel}` +
        ` may or may not have been ${pastTense}. ${check}`,
    }
  }

  const detail =
    error instanceof Error ? error.message : toDisplayMessage(error)
  return {
    type: 'warning',
    message: `Couldn't ${verb} ${entityLabel} (${detail})`,
  }
}
